// Pipe Take-off — project detail. Tabs: Items (editable), Summary
// (consolidated rollup), PDF (source preview), and Admin (raw
// extraction diagnostics, gated on isAdmin). Items mutations bump
// summaryReloadKey so the Summary tab re-fetches on next view.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { api } from "../../../lib/api";
import { formatDate } from "../../../lib/utils";
import { useAuth } from "../../../auth/useAuth";
import type {
  TakeoffItemDto,
  TakeoffProjectDto,
} from "../../../../../shared/types/pipe-takeoff";
import { ItemsTable } from "./ItemsTable";
import { ConsolidatedSummary } from "./ConsolidatedSummary";
import { PdfPreview } from "./PdfPreview";
import { ProjectHeader } from "./ProjectHeader";
import { AdminTab } from "./AdminTab";
import { PerSpoolTab } from "./PerSpoolTab";

type Tab = "items" | "summary" | "spools" | "pdf" | "admin";

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const { isAdmin } = useAuth();
  const [project, setProject] = useState<TakeoffProjectDto | null>(null);
  const [items, setItems] = useState<TakeoffItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("items");
  const [summaryReloadKey, setSummaryReloadKey] = useState(0);

  useEffect(() => {
    if (!Number.isFinite(projectId)) {
      setError("Invalid project id");
      return;
    }
    void (async () => {
      try {
        const [p, i] = await Promise.all([
          api<TakeoffProjectDto>(`/api/tools/pipe-takeoff/projects/${projectId}`),
          api<{ items: TakeoffItemDto[] }>(
            `/api/tools/pipe-takeoff/projects/${projectId}/items`,
          ),
        ]);
        setProject(p);
        setItems(i.items);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [projectId]);

  const upsertItem = (next: TakeoffItemDto) => {
    setItems((prev) => prev?.map((it) => (it.id === next.id ? next : it)) ?? [next]);
    setSummaryReloadKey((k) => k + 1);
  };
  const appendItem = (item: TakeoffItemDto) => {
    setItems((prev) => (prev ? [...prev, item] : [item]));
    setSummaryReloadKey((k) => k + 1);
  };
  const removeItem = (rid: number) => {
    setItems((prev) => prev?.filter((it) => it.id !== rid) ?? []);
    setSummaryReloadKey((k) => k + 1);
  };

  return (
    <>
      <ProjectHeader project={project} projectId={projectId} />

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {project && project.status !== "completed" && (
        <div className="bsp-card" style={{ marginBottom: 16 }}>
          <p style={{ margin: 0 }}>
            Status: <strong>{project.status}</strong>{" "}
            {project.status !== "failed" && (
              <Link to={`/tools/pipe-takeoff/${projectId}/extract`}>View progress</Link>
            )}
          </p>
          {project.errorMessage && (
            <p style={{ color: "var(--bsp-red-fg)", margin: "8px 0 0" }}>
              {project.errorMessage}
            </p>
          )}
        </div>
      )}

      <div role="tablist" style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--bsp-border)", marginBottom: 16 }}>
        {(
          ["items", "summary", "spools", "pdf", ...(isAdmin ? (["admin"] as const) : [])] as Tab[]
        ).map((t) => (
          <button
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            className="bsp-btn bsp-btn-ghost"
            style={{
              borderRadius: 0,
              borderBottom: tab === t ? "2px solid var(--bsp-blue)" : "2px solid transparent",
              color: tab === t ? "var(--bsp-blue)" : "var(--bsp-fg-muted)",
            }}
            onClick={() => setTab(t)}
          >
            {t === "items"
              ? "Items"
              : t === "summary"
                ? "Summary"
                : t === "spools"
                  ? "Per spool"
                  : t === "pdf"
                    ? "PDF"
                    : "Admin"}
          </button>
        ))}
      </div>

      {tab === "items" && items !== null && (
        <ItemsTable
          projectId={projectId}
          items={items}
          onChanged={upsertItem}
          onAdded={appendItem}
          onDeleted={removeItem}
        />
      )}
      {tab === "summary" && (
        <ConsolidatedSummary projectId={projectId} reloadKey={summaryReloadKey} />
      )}
      {tab === "spools" && items !== null && <PerSpoolTab items={items} />}
      {tab === "pdf" && project?.pdfBlobPath && <PdfPreview projectId={projectId} />}
      {tab === "pdf" && !project?.pdfBlobPath && (
        <p style={{ color: "var(--bsp-fg-muted)" }}>No PDF uploaded for this project.</p>
      )}
      {tab === "admin" && isAdmin && project && <AdminTab project={project} />}

      {project && (
        <p style={{ color: "var(--bsp-fg-muted)", fontSize: 12, marginTop: 12 }}>
          Created {formatDate(project.createdAt)}
        </p>
      )}
    </>
  );
}
